import Cookies from 'mj-cookie'

// issue #721: 特定の外部LPからの遷移を識別し、dataLayer経由でGTMに連携する仕組み。
//
// 初出はバームクーヘン社の広告ピクセル(Meta/TikTok)対応だが、用途(ピクセル発火)やベンダーに
// 依存しないよう「どのLPから遷移してきたか」という事実(LPのID)だけを扱う汎用実装にしている。
// LPごとにユニークなIDをCookieに保存し、有効なID一覧を返すところまでを担う。
//
// 返した値(lp_ids / lp_ids_str)は GTM.defaultData に載せて、既存のページビューイベント
// `nuxtRoute` の直前にdataLayerへpushされる。これによりハードロード・SPA遷移の双方、かつ
// (コンポーネント側でnuxtRouteを撃つトップ/一覧/詳細等も含め)全ページで値が揃う。
// 実際のピクセル発火やID→ピクセルIDの紐付けは、自社GTM(GTM-TDNXC9)側で既存の`nuxtRoute`
// トリガーに `lp_ids_str が |<id>| を含む` 条件を足して行う(開発不要で増減可能)。
//
// 遷移パラメータ: ?lp_id=<lpId>
//   ※ issue本文では ?from=yuenchi と記載があるが、'from' は既存の遷移制御(dating_events等)で
//     使用済みのため衝突・Cookieノイズを避け、汎用的な専用キー 'lp_id' を使用する。
//     LP側の付与パラメータを最終確定する際は、この定数を合わせること。
export const LP_TRACKING_QUERY_KEY = 'lp_id'

// LP IDごとに1本作るCookieのprefix。
export const LP_TRACKING_COOKIE_PREFIX = 'mcj_lp_id_'

// Cookieの有効期限(日)。issue要件: 最大90日。LP IDごとに独立して90日保持する。
export const LP_TRACKING_EXPIRES_DAYS = 90

// 1ユーザーが保持するLP ID Cookieの上限。外部リンクで大量のユニークlp_idを渡されても
// ブラウザのCookie数/リクエストヘッダーサイズの上限を圧迫しないよう総数を縛る。
// 上限超過時は最終アクセスが古いIDから淘汰する(最新のLPは必ず記録される)。
export const LP_TRACKING_MAX_IDS = 30

// 想定外の値でCookieを量産しないためのID書式チェック(英数字・ハイフン・アンダースコアのみ)。
const VALID_ID = /^[a-zA-Z0-9_-]{1,40}$/

export default class LpTracking {
  // LP遷移時: クエリのLP IDを検知して、IDごとのCookie(90日)に保存する。
  static setAccessInfo(route) {
    const raw = route.query[LP_TRACKING_QUERY_KEY]
    const id = Array.isArray(raw) ? raw[0] : raw
    if (!id || !VALID_ID.test(id)) return
    const expires = new Date(new Date().getTime() + LP_TRACKING_EXPIRES_DAYS * 864e5)
    // 値は最終アクセス時刻(秒)。上限超過時に古い順で淘汰するために使う。
    const lastAccessedAt = Math.round(new Date().getTime() / 1000)
    Cookies.set(`${LP_TRACKING_COOKIE_PREFIX}${id}`, String(lastAccessedAt), { expires, path: '/' })
    this.enforceMaxIds()
  }

  // LP ID Cookieの総数が上限を超えたら、最終アクセスが古いものから削除する。
  static enforceMaxIds() {
    const all = Cookies.get() || {}
    const entries = Object.keys(all)
      .filter(name => name.startsWith(LP_TRACKING_COOKIE_PREFIX))
      .map(name => ({ name, lastAccessedAt: parseInt(all[name], 10) || 0 }))
    if (entries.length <= LP_TRACKING_MAX_IDS) return
    entries
      .sort((a, b) => a.lastAccessedAt - b.lastAccessedAt)
      .slice(0, entries.length - LP_TRACKING_MAX_IDS)
      .forEach(entry => Cookies.remove(entry.name, { path: '/' }))
  }

  // 現在有効な(=Cookieが存在する)LP ID一覧を返す。90日を過ぎたCookieはブラウザが自動失効する。
  static activeIds() {
    const all = Cookies.get() || {}
    return Object.keys(all)
      .filter(name => name.startsWith(LP_TRACKING_COOKIE_PREFIX))
      .map(name => name.slice(LP_TRACKING_COOKIE_PREFIX.length))
      .filter(id => VALID_ID.test(id))
  }

  // GTM.defaultDataに載せるデータを返す。着地時のCookie保存も行う。
  // GTM側は既存の`nuxtRoute`トリガーで lp_ids_str をcontains判定してピクセルを発火する。
  static gtmData(route) {
    this.setAccessInfo(route)
    const ids = this.activeIds()
    if (ids.length === 0) return {}
    return {
      lp_ids: ids, // 配列(複数LP経由なら全件保持)
      lp_ids_str: `|${ids.join('|')}|` // GTMのcontains判定用(区切り付きで部分一致の誤爆を防ぐ)
    }
  }
}
